import React, { useState } from 'react';
import { Trophy, ArrowUpDown, Search } from 'lucide-react';
import { LeaderboardEntry } from './LeaderboardEntry';
import { LeaderboardSearch } from './LeaderboardSearch';
import { LeaderboardPagination } from './LeaderboardPagination';
import { useSupabaseLeaderboard } from '../../hooks/useSupabaseLeaderboard';

const ITEMS_PER_PAGE = 20; // Increased from 10 to show more users

type SortField = 'points' | 'username' | 'lastUpdated';
type SortDirection = 'asc' | 'desc';

export function LeaderboardList() {
  const { entries, loading, error } = useSupabaseLeaderboard();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('points');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-400 py-12">
        <p>{error}</p>
      </div>
    );
  }

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredEntries = entries
    .filter(entry =>
      entry.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.walletAddress.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const modifier = sortDirection === 'asc' ? 1 : -1;
      switch (sortField) {
        case 'points':
          return (a.points - b.points) * modifier;
        case 'username':
          return a.username.localeCompare(b.username) * modifier;
        case 'lastUpdated':
          return (new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime()) * modifier;
        default:
          return 0;
      }
    });

  const totalPages = Math.ceil(filteredEntries.length / ITEMS_PER_PAGE);
  const paginatedEntries = filteredEntries.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 mb-8">
        <h2 className="text-2xl font-bold font-serif text-yellow-500 flex items-center gap-2">
          <Trophy className="w-6 h-6" />
          Hall of Champions
        </h2>
        <LeaderboardSearch value={searchQuery} onChange={setSearchQuery} />
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-4 gap-4 p-4 bg-gray-700 text-sm font-medium text-gray-300">
          <button
            onClick={() => handleSort('username')}
            className="flex items-center gap-2 hover:text-white"
          >
            Champion
            {sortField === 'username' && (
              <ArrowUpDown className={`w-4 h-4 ${sortDirection === 'asc' ? 'rotate-180' : ''}`} />
            )}
          </button>
          <div>Wallet</div>
          <button
            onClick={() => handleSort('points')}
            className="flex items-center gap-2 hover:text-white"
          >
            Points
            {sortField === 'points' && (
              <ArrowUpDown className={`w-4 h-4 ${sortDirection === 'asc' ? 'rotate-180' : ''}`} />
            )}
          </button>
          <button
            onClick={() => handleSort('lastUpdated')}
            className="flex items-center gap-2 hover:text-white"
          >
            Last Active
            {sortField === 'lastUpdated' && (
              <ArrowUpDown className={`w-4 h-4 ${sortDirection === 'asc' ? 'rotate-180' : ''}`} />
            )}
          </button>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-gray-700">
          {paginatedEntries.map((entry, index) => (
            <LeaderboardEntry
              key={entry.walletAddress}
              entry={{
                ...entry,
                rank: (currentPage - 1) * ITEMS_PER_PAGE + index + 1
              }}
            />
          ))}
        </div>
      </div>

      <div className="mt-6">
        <LeaderboardPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      <div className="text-center text-sm text-gray-400 mt-4">
        Total Champions: {filteredEntries.length}
      </div>
    </div>
  );
}